import React, { useEffect, useState, useMemo, useCallback } from "react";

import { Grid, MenuItem, SelectChangeEvent } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { parseISO } from "date-fns";
import { useTranslation } from "react-i18next";
import { FiTrash2 } from "react-icons/fi";
import isEmail from "validator/lib/isEmail";
import * as yup from "yup";
import { ObjectShape } from "yup/lib/object";

import { Input } from "../../../../../../../components/Form";
import AutoSuggest from "../../../../../../../components/Form/AutoSuggest";
import Button, { Color } from "../../../../../../../components/Form/Button";
import TextField from "../../../../../../../components/Form/Input";
import PhoneInput from "../../../../../../../components/Form/PhoneInput";
import Select from "../../../../../../../components/Form/Select";
import ActionButtons from "../../../../../../../components/Generic/ActionButtons";
import ListContacts from "../../../../../../../components/Generic/ListContacts";
import ListMainLocation from "../../../../../../../components/Generic/ListMainLocation";
import Modal from "../../../../../../../components/Generic/Modal";
import DeleteModal from "../../../../../../../components/Generic/Modal/Delete";
import LoadingModal from "../../../../../../../components/LoadingModal";
import { useMovingResources } from "../../../../../../../context/MovingResources";
import { useAppState } from "../../../../../../../context/StateContext";
import { useCustomerHook } from "../../../../../../../hooks/useCustomerHook";
import useYupValidation from "../../../../../../../hooks/useYupValidation";
import { AutomaticSequencing } from "../../../../../../../models/automatic.sequencing";
import { Customer } from "../../../../../../../models/customer";
import { Error } from "../../../../../../../models/error";
import { Location } from "../../../../../../../models/location";
import { Timezone } from "../../../../../../../models/timezone";
import { User } from "../../../../../../../models/user";
import { Warehouse } from "../../../../../../../models/warehouse";
import OrderService from "../../../../../../../services/order.service";
import TaskService from "../../../../../../../services/task.service";
import TimezoneService from "../../../../../../../services/timezone.service";
import UserService from "../../../../../../../services/user.service";
import WarehouseService from "../../../../../../../services/warehouse.service";
import { Status, CustomerType, UserTypes } from "../../../../../../../util/enums";
import { formatTimezone } from "../../../../../../../util/Format";
import { formatInTimeZone } from "../../../../../../../util/formatInTimezone";
import {
  phoneInputSettings,
  phoneNumberIsValid,
} from "../../../../../../../util/formatPhoneNumber";
import { UserKeys, WarehouseKeys } from "../../../../../../../util/queries";
import { validateBeCode } from "../../../../../../../util/validateBeCode";
import useStyles from "./styles";

type Props = {
  viewOnly?: boolean;
  onUpdate?: (customer?: Customer) => void;
  onDelete?: () => void;
  onCreate?: () => void;
  goBack?: () => void;
  modalMode?: boolean;
  isLoadingCreate?: boolean;
  isLoadingUpdate?: boolean;
  isLoadingCustomer?: boolean;
  open?: boolean;
  sequence?: AutomaticSequencing;
  responseError?: Error;
  isDelivery?: boolean;
};

type KeyOfCustomer = keyof Customer;

const DetailsComponent = (props: Props) => {
  const {
    onUpdate,
    modalMode,
    onDelete,
    onCreate,
    viewOnly,
    goBack,
    isLoadingCreate,
    isLoadingUpdate,
    isLoadingCustomer,
    open = false,
    sequence,
    responseError,
    isDelivery = false,
  } = props;

  const { currentUser, isFMorSPVersion } = useAppState();

  const [deleteModalIsOpen, setDeleteModalIsOpen] = useState<boolean>(false);

  const movingResources = useMovingResources();

  const { customer, setCustomer, handleUpdateCustomer } = useCustomerHook();

  const { t: translate } = useTranslation();

  const [isSubmitted, setIsSubmitted] = useState<boolean>();

  const [errors, setErrors] = useState<yup.ValidationError[]>([]);
  const { fieldHasError, getErrorByField } = useYupValidation(errors);

  const [consultants, setConsultants] = useState<User[]>();
  const [timezones, setTimezones] = useState<Timezone[]>(movingResources?.timezoneList || []);
  const [, setWarehouses] = useState<Warehouse[]>();

  const [totalCustomerQuotes, setTotalCustomerQuotes] = useState<number | null>(null);
  const [totalCustomerOrders, setTotalCustomerOrders] = useState<number | null>(null);

  const classes = useStyles();

  useEffect(() => {
    if (isSubmitted) {
      if (modalMode) {
        handleValidateModal();
      } else {
        handleValidate();
      }
    }
  }, [customer]);

  useEffect(() => {
    if (sequence && !customer.code)
      handleUpdateCustomer(
        "code",
        `${sequence?.prefix ? sequence.prefix : ""}${sequence?.nextValue ? sequence.nextValue : ""}`
      );
  }, [sequence]);

  useQuery(
    ["timezones.list"],
    () => TimezoneService.getAll({ sortBy: JSON.stringify(["gmtOffset"]) }),
    {
      enabled: !movingResources?.timezoneList,
      onSuccess: (response) => {
        if (response && response.data && response.data?.length > 0) setTimezones(response.data);
      },
    }
  );

  useQuery({
    queryKey: WarehouseKeys.getAll([currentUser?.companyId]),
    queryFn: () =>
      WarehouseService.getAll({
        companyId: currentUser?.companyId,
      }),
    enabled: !movingResources?.timezoneList,
    onSuccess: (response) => {
      if (response && response.data && response.data?.length > 0) setWarehouses(response.data);
    },
  });

  const userCompaniesAssociation = {
    association: "usersCompanies",
    required: true,
    where: {
      companyId: currentUser?.companyId,
      typeUser: [UserTypes.ADMIN, UserTypes.SALES],
      status: Status.ACTIVE,
    },
  };

  useQuery({
    queryKey: UserKeys.getAll([currentUser?.companyId, userCompaniesAssociation]),
    queryFn: () =>
      UserService.getAll({
        companyId: currentUser?.companyId,
        isAdmin: currentUser?.isAdmin ? [0, 1] : false,
        include: JSON.stringify([userCompaniesAssociation]),
        sortBy: JSON.stringify([["full_name", "asc"]]),
      }),
    enabled: !!currentUser?.companyId,
    onSuccess: (response) => {
      if (response && response.data && response.data?.length > 0) setConsultants(response.data);
    },
  });

  useQuery(
    ["quotes.count", customer.id],
    () =>
      TaskService.count({
        customerId: customer.id,
        userId: currentUser?.id,
        type: "Video Survey",
      }),
    {
      enabled: !!customer?.id && !movingResources?.timezoneList,
      onSuccess: (response) => setTotalCustomerQuotes(response.data),
    }
  );

  useQuery(
    ["orders.count", customer.id],
    () =>
      OrderService.count({
        customerId: customer.id,
        userId: currentUser?.id,
      }),
    {
      enabled: !!customer?.id && !movingResources?.timezoneList,
      onSuccess: (response) => setTotalCustomerOrders(response.data),
    }
  );

  const onClickSave = () => {
    setIsSubmitted(true);
    if (modalMode) {
      if (handleValidateModal()) submit();
    } else if (handleValidate()) submit();
  };

  const submit = () => {
    if (customer && customer?.id) {
      if (onUpdate) onUpdate();
    } else if (onCreate) onCreate();
  };

  const onClickDelete = () => {
    setDeleteModalIsOpen(true);
  };

  const handleValidateModal = (): boolean => {
    try {
      const additions = {
        name: yup.string().required(translate("youMustEnterACustomer")),
        email: yup
          .string()
          .required(translate("youMustEnterAEmail"))
          .test({
            name: "validate-email",
            message: translate("pleaseEnterAValidEmail"),
            test: (value) => {
              return isEmail(value || "");
            },
          }),
        timezone: yup.number().required(translate("youMustEnterATimezone")).nullable(),
      } as ObjectShape;

      const schema = yup.object().shape(additions);

      schema.validateSync(
        {
          abbreviation: customer?.abbreviation,
          name: customer?.name,
          hasOnlyMobile: !customer?.phone?.trim() && !!customer?.mobile?.trim(),
          email: customer?.email,
          timezone: customer?.timezoneId,
        },
        { abortEarly: false }
      );

      setErrors([]);
    } catch (error) {
      if (error instanceof yup.ValidationError) setErrors(error?.inner);

      return false;
    }
    if (errors.length !== 0) return false;

    return true;
  };

  const schema = yup.object().shape({
    name: yup
      .string()
      .required(
        customer?.type === CustomerType.CORPORATE
          ? translate("youMustEnterAnEntityName")
          : translate("youMustEnterAFirstName")
      ),
    email: yup
      .string()
      .required(translate("youMustEnterAEmail"))
      .typeError(translate("youMustEnterAEmail")),
    timezone: yup.string().required(translate("youMustEnterATimezone")).nullable(),
    isCustomerPrivate: yup.boolean(),
    isCustomerCorporate: yup.boolean(),
    lastName: yup
      .string()
      .when("isCustomerPrivate", {
        is: true,
        then: yup.string().required(translate("youMustEnterALastName")).nullable(),
      })
      .nullable(),
    ...(isFMorSPVersion
      ? {
          beCode: yup
            .string()
            .typeError(translate("thisFieldIsRequired"))
            .test({
              name: "validate-beCode",
              message: translate("pleaseEnteraValidBecode"),
              test: (value) => {
                return validateBeCode(value, isFMorSPVersion);
              },
            }),
        }
      : {
          consultant: yup.string().required(translate("youMustEnterAConsultant")).nullable(),
          registrationNumber: yup
            .string()
            .when("isCustomerCorporate", {
              is: true,
              then: yup.string().required(translate("youMustEnterARegistrationNumber")).nullable(),
            })
            .nullable(),
        }),
  });

  const handleValidate = (): boolean => {
    try {
      schema.validateSync(
        {
          name: customer?.name,
          lastName: customer?.lastName,
          registrationNumber: customer?.registrationNumber,
          email: customer?.email,
          consultant: customer?.consultantId,
          timezone: customer?.timezoneId,
          isCustomerPrivate: customer?.type === CustomerType.PRIVATE,
          isCustomerCorporate: customer?.type === CustomerType.CORPORATE,
          beCode: customer?.beCode,
        },
        { abortEarly: false }
      );
      setErrors([]);

      return true;
    } catch (error) {
      if (error instanceof yup.ValidationError) {
        setErrors(error?.inner);
      }

      return false;
    }
  };

  const customerHasOrdersOrQuotes = useMemo(() => {
    if (totalCustomerQuotes === -1 && totalCustomerOrders === -1) return true;

    if (totalCustomerOrders === null || totalCustomerQuotes === null) return true;

    return !!(totalCustomerQuotes + totalCustomerOrders);
  }, [totalCustomerQuotes, totalCustomerOrders]);

  const updateCustomerWithDefaultValue = useCallback(() => {
    if (!customer.consultantId && !isFMorSPVersion) {
      handleUpdateCustomer("consultant", currentUser);
      handleUpdateCustomer("consultantId", currentUser?.id);
    }

    if (!customer.timezoneId) {
      const currentTimezone: string = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const timezoneFound = timezones?.find((timezone) => timezone.name === currentTimezone);

      if (timezoneFound && timezoneFound?.nameFormatted) {
        handleUpdateCustomer("timezone", timezoneFound);
        handleUpdateCustomer("timezoneId", timezoneFound.id);
      }
    }
  }, [timezones, customer]);

  useEffect(() => {
    if (!customer?.id) {
      updateCustomerWithDefaultValue();
    }
  }, [timezones, customer]);

  const renderFieldCorporate = () => {
    if (customer?.type && customer?.type === CustomerType.CORPORATE) {
      return (
        <>
          <Grid item xs={12} sm={6}>
            <TextField
              label={translate("entity")}
              placeholder={translate("entityName")}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                handleUpdateCustomer("name", event.target.value)
              }
              value={customer?.name}
              error={fieldHasError("name")}
              errorMessage={getErrorByField("name")}
              data-cy="entity"
              disabled={viewOnly}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label={translate("registrationNumber")}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                handleUpdateCustomer("registrationNumber", event.target.value)
              }
              value={customer?.registrationNumber}
              error={fieldHasError("registrationNumber")}
              errorMessage={getErrorByField("registrationNumber")}
              data-cy="registration"
              disabled={viewOnly}
            />
          </Grid>
        </>
      );
    }

    return null;
  };

  const renderFieldPrivate = () => {
    if (!customer?.type || (customer?.type && customer?.type === CustomerType.PRIVATE)) {
      return (
        <>
          <Grid item xs={12} sm={6}>
            <TextField
              placeholder={translate("firstName")}
              label={translate("firstName")}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                handleUpdateCustomer("name", event.target.value)
              }
              value={customer?.name}
              error={fieldHasError("name")}
              errorMessage={getErrorByField("name")}
              data-cy="first-name"
              disabled={viewOnly}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              placeholder={translate("lastName")}
              label={translate("lastName")}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                handleUpdateCustomer("lastName", event.target.value)
              }
              value={customer?.lastName}
              error={fieldHasError("lastName")}
              errorMessage={getErrorByField("lastName")}
              data-cy="last-name"
              disabled={viewOnly}
            />
          </Grid>
        </>
      );
    }

    return null;
  };

  const onChangeCustomerType = (type: CustomerType) => {
    const defaultAttributesToChange: KeyOfCustomer[] = [
      "name",
      "contacts",
      "email",
      "destinationMobile",
      "mobile",
      "phone",
      "locations",
    ];
    const attributesToChangeOnTypePrivate: KeyOfCustomer[] = ["lastName"];
    const attributesToChangeOnTypeCorporate: KeyOfCustomer[] = ["registrationNumber"];

    const attributesToClear = [
      ...defaultAttributesToChange,
      ...(type === CustomerType.PRIVATE
        ? attributesToChangeOnTypePrivate
        : attributesToChangeOnTypeCorporate),
    ];

    const updatedCustomer = attributesToClear.reduce<Partial<Customer>>(
      (acc, attr) => {
        acc[attr] = undefined;

        return acc;
      },
      { ...customer, type }
    );

    setCustomer(updatedCustomer as Customer);
  };

  const renderDeleteButton = () => {
    if (
      currentUser &&
      currentUser?.typeUser === "Admin" &&
      customer &&
      customer?.id &&
      !customerHasOrdersOrQuotes
    ) {
      return (
        <Button
          disabled={viewOnly || isLoadingCreate || isLoadingUpdate}
          onClick={onClickDelete}
          startIcon={<FiTrash2 className={classes.icon} color="#FFFFFF" />}
          customColor={Color.ERROR}
        >
          {translate("deleteCustomer")}
        </Button>
      );
    }

    return null;
  };

  const renderFooter = () => {
    if (!modalMode) {
      return (
        <Grid item container xs={12} justifyContent="space-between">
          <Grid item xs={6}>
            {renderDeleteButton()}
          </Grid>
          <Grid item direction="row">
            <ActionButtons
              dataCyCancel="customer-cancel-button"
              cancelDisabled={isLoadingCreate || isLoadingUpdate}
              onCancel={() => goBack && goBack()}
              saveDisabled={isLoadingCreate || isLoadingUpdate || viewOnly}
              dataCySave="button-save"
              onSave={onClickSave}
              textSave={customer && customer?.id ? translate("save") : translate("create")}
            />
          </Grid>
          <LoadingModal showing={isLoadingCreate || isLoadingUpdate} />
        </Grid>
      );
    }
  };

  const renderForm = () => {
    return (
      <Grid container item xs={12} direction="row">
        <Grid container item xs={12} spacing={2} direction="row" className={classes.formContent}>
          <Grid item xs={12}>
            <span className={classes.title}>{translate("customerDetails")}</span>
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField
              name="customer.code"
              type="text"
              label={`${translate("customer")} #`}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                handleUpdateCustomer("code", event.target.value)
              }
              value={customer?.code || ""}
              data-cy="code"
              disabled={viewOnly}
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField
              name="customer.since"
              type="date"
              label={
                customer?.type?.toLowerCase() === "corporate"
                  ? translate("established")
                  : translate("since")
              }
              onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                handleUpdateCustomer(
                  "since",
                  formatInTimeZone(
                    "yyyy-MM-dd'T'HH:mm:ss.SSSxxx",
                    "UTC",
                    parseISO(event.target.value)
                  )
                )
              }
              value={formatInTimeZone("yyyy-MM-dd", "UTC", customer?.since)}
              data-cy="since"
              disabled={viewOnly}
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <Select
              name="customer.status"
              label={translate("status")}
              onChange={(event: SelectChangeEvent<any>) =>
                handleUpdateCustomer("status", event.target.value)
              }
              value={customer?.status}
              data-cy="status"
              disabled={viewOnly}
              placeholder={translate("select")}
            >
              <MenuItem value="Active">{translate("active")}</MenuItem>
              <MenuItem value="Inactive">{translate("inactive")}</MenuItem>
            </Select>
          </Grid>
          <Grid item xs={12} sm={3}>
            <Select
              name="customer.type"
              label={translate("type")}
              onChange={(event: SelectChangeEvent<any>) => {
                handleUpdateCustomer("type", event.target.value);
                onChangeCustomerType(event.target.value as CustomerType);
              }}
              value={customer?.type}
              data-cy="type"
              disabled={viewOnly}
              placeholder={translate("select")}
            >
              <MenuItem value={CustomerType.PRIVATE}>{translate("private")}</MenuItem>
              <MenuItem value={CustomerType.CORPORATE}>{translate("corporate")}</MenuItem>
            </Select>
          </Grid>

          {renderFieldCorporate()}
          {renderFieldPrivate()}

          {isFMorSPVersion && (
            <Grid item xs={6} md={3}>
              <Input
                name="customer_beCode"
                data-cy="beCode"
                label={translate("beCode")}
                placeholder={translate("beCodeCustomerExample")}
                value={customer?.beCode}
                disabled={viewOnly}
                onChange={(event) => handleUpdateCustomer("beCode", event.target.value)}
                error={fieldHasError("beCode")}
                errorMessage={getErrorByField("beCode")}
                required
              />
            </Grid>
          )}

          <Grid item xs={12}>
            <span className={classes.title}>{translate("primaryContact")}</span>
          </Grid>
          <Grid item xs={12}>
            <ListContacts
              viewOnly={viewOnly}
              contacts={customer?.contacts}
              onChange={(contacts) => handleUpdateCustomer("contacts", contacts)}
              timezones={timezones}
            />
          </Grid>
          <Grid item xs={12}>
            <span className={classes.title}>{translate("billingDetails")}</span>
            <span className={classes.subtitle}>
              {translate("pleaseFillInEitherTheMobileNumberOrPhoneNumberField")}
            </span>
          </Grid>

          <Grid item xs={12} sm={3}>
            <TextField
              label={translate("billingEmail")}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                handleUpdateCustomer("email", event.target.value)
              }
              name="customer.email"
              value={customer?.email}
              error={fieldHasError("email")}
              errorMessage={getErrorByField("email")}
              errorResponse={responseError}
              data-cy="billing-email"
              disabled={viewOnly}
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <PhoneInput
              label={translate("mobile")}
              onChange={(value: string) => {
                if (phoneNumberIsValid(value)) {
                  handleUpdateCustomer(isDelivery ? "destinationMobile" : "mobile", value);
                }
              }}
              placeholder="61430111900"
              value={isDelivery ? customer?.destinationMobile : customer?.mobile}
              {...phoneInputSettings(currentUser?.company)}
              name={isDelivery ? "customer.destinationMobile" : "customer.phone"}
              error={fieldHasError("mobile")}
              errorMessage={getErrorByField("mobile")}
              errorResponse={responseError}
              data-cy="mobile"
              disabled={viewOnly}
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <PhoneInput
              label={translate("phoneNumber")}
              onChange={(value: string) => {
                if (phoneNumberIsValid(value)) {
                  handleUpdateCustomer("phone", value);
                }
              }}
              placeholder="61430111900"
              value={customer?.phone}
              {...phoneInputSettings(currentUser?.company)}
              name={"customer.phone"}
              error={fieldHasError("phone")}
              errorMessage={getErrorByField("phone")}
              errorResponse={responseError}
              data-cy="mobile"
              disabled={viewOnly}
            />
          </Grid>

          <Grid item xs={12}>
            <ListMainLocation
              locations={customer?.locations as Location[]}
              viewOnly={viewOnly}
              onChange={(locations) => handleUpdateCustomer("locations", locations)}
              showHeader={customer?.type === "Corporate"}
            />
          </Grid>

          <Grid item xs={12}>
            <span className={classes.title}>{translate("otherDetails")}</span>
          </Grid>

          <Grid item xs={12} sm={6}>
            <AutoSuggest
              label={translate("consultant")}
              placeholder={translate("searchConsultant")}
              options={consultants}
              data-cy="consultant"
              disabled={viewOnly}
              value={customer?.consultant}
              inputValue={
                customer?.consultant
                  ? `${customer?.consultant?.name} ${customer?.consultant?.lastName} - ${customer?.consultant?.email}`
                  : ""
              }
              onChange={(consultant: User) => {
                handleUpdateCustomer("consultant", consultant ?? null);
                handleUpdateCustomer("consultantId", consultant?.id ?? null);
              }}
              error={fieldHasError("consultant")}
              errorMessage={getErrorByField("consultant")}
              getOptionLabel={(consultant?: User) => {
                const name = consultant?.name || "";
                const lastName = consultant?.lastName || "";
                const email = consultant?.email || "";

                return `${name} ${lastName} - ${email}`;
              }}
              getOptionSelected={(option: User, value: User) => option?.id === value?.id}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <AutoSuggest
              label={translate("timezone")}
              placeholder={translate("searchTimezone")}
              data-cy="timezone"
              options={timezones}
              disabled={viewOnly}
              inputValue={customer?.timezone ? formatTimezone(customer?.timezone) : ""}
              value={timezones ? customer?.timezone : undefined}
              onChange={(timezone?: Timezone) => {
                handleUpdateCustomer("timezone", timezone);
                handleUpdateCustomer("timezoneId", timezone?.id || null);
              }}
              error={fieldHasError("timezone")}
              errorMessage={getErrorByField("timezone")}
              getOptionLabel={(timezone: Timezone) => formatTimezone(timezone)}
              getOptionSelected={(option: Timezone, value: Timezone) => option?.id === value?.id}
            />
          </Grid>

          {renderFooter()}
        </Grid>
      </Grid>
    );
  };

  const renderContent = () => {
    if (modalMode) {
      return (
        <Modal
          actionButtonProps={{
            onSave: onClickSave,
            cancelDisabled: isLoadingCreate || isLoadingUpdate,
            loading: isLoadingCreate || isLoadingUpdate,
            saveDisabled: isLoadingCustomer,
          }}
          handleClose={() => goBack && goBack()}
          showActionButtons
          open={open}
          title={translate("editCustomer")}
          className={classes.infoModal}
        >
          <Grid container spacing={2} direction="row">
            <Grid container item spacing={2} direction="column">
              <Grid container item alignContent="flex-start" spacing={2} direction="row">
                <Grid item xs={12} sm={1}>
                  <TextField
                    label={translate("salutation")}
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                      handleUpdateCustomer("abbreviation", event.target.value)
                    }
                    value={customer?.abbreviation}
                    error={fieldHasError("abbreviation")}
                    errorMessage={getErrorByField("abbreviation")}
                    disabled={viewOnly}
                  />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <TextField
                    label={translate("name")}
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                      handleUpdateCustomer("name", event.target.value)
                    }
                    value={customer?.name}
                    error={fieldHasError("name")}
                    errorMessage={getErrorByField("name")}
                    disabled={viewOnly}
                  />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <TextField
                    label={translate("lastName")}
                    onChange={(event) => handleUpdateCustomer("lastName", event.target.value)}
                    value={customer?.lastName}
                    error={fieldHasError("lastName")}
                    errorMessage={getErrorByField("lastName")}
                    disabled={viewOnly}
                  />
                </Grid>
              </Grid>
            </Grid>

            <Grid container item alignContent="flex-start" spacing={2} direction="row">
              <Grid item xs={12} sm={4}>
                <PhoneInput
                  label={translate("mobile")}
                  onChange={(value: string) => {
                    if (phoneNumberIsValid(value)) {
                      handleUpdateCustomer(isDelivery ? "destinationMobile" : "mobile", value);
                    }
                  }}
                  placeholder="61430111900"
                  value={isDelivery ? customer?.destinationMobile : customer?.mobile}
                  {...phoneInputSettings(currentUser?.company)}
                  name={isDelivery ? "customer.destinationMobile" : "customer.phone"}
                  error={fieldHasError(isDelivery ? "destinationMobile" : "mobile")}
                  errorMessage={getErrorByField(isDelivery ? "destinationMobile" : "mobile")}
                  errorResponse={responseError}
                  data-cy="mobile"
                  disabled={viewOnly}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <PhoneInput
                  label={translate("phoneNumber")}
                  onChange={(value: string) => {
                    if (phoneNumberIsValid(value)) {
                      handleUpdateCustomer("phone", value);
                    }
                  }}
                  placeholder="61430111900"
                  value={customer?.phone}
                  {...phoneInputSettings(currentUser?.company)}
                  name={"customer.phone"}
                  error={fieldHasError("phone")}
                  errorMessage={getErrorByField("phone")}
                  errorResponse={responseError}
                  data-cy="phone"
                  disabled={viewOnly}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  label={translate("email")}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                    handleUpdateCustomer("email", event.target.value)
                  }
                  value={customer?.email}
                  error={fieldHasError("email")}
                  errorMessage={getErrorByField("email")}
                  data-cy="billing-email"
                  disabled={viewOnly}
                />
              </Grid>
            </Grid>

            <Grid container item alignContent="flex-start" spacing={2} direction="row">
              <Grid item xs={12} sm={4}>
                <AutoSuggest
                  label={translate("timezone")}
                  placeholder={translate("searchTimezone")}
                  data-cy="timezone"
                  options={timezones}
                  disabled={viewOnly}
                  inputValue={customer?.timezone ? formatTimezone(customer?.timezone) : ""}
                  value={timezones ? customer?.timezone : undefined}
                  onChange={(timezone?: Timezone) => {
                    handleUpdateCustomer("timezone", timezone);
                    handleUpdateCustomer("timezoneId", timezone?.id || null);
                  }}
                  error={fieldHasError("timezone")}
                  errorMessage={getErrorByField("timezone")}
                  getOptionLabel={(timezone: Timezone) => formatTimezone(timezone)}
                  getOptionSelected={(option: Timezone, value: Timezone) =>
                    option?.id === value?.id
                  }
                />
              </Grid>
            </Grid>
          </Grid>
        </Modal>
      );
    }

    return renderForm();
  };

  return (
    <div>
      <DeleteModal
        name="customer"
        open={deleteModalIsOpen}
        handleClose={(isDelete) => {
          if (isDelete && onDelete) onDelete();
          setDeleteModalIsOpen(false);
        }}
      />
      {renderContent()}
    </div>
  );
};

export default DetailsComponent;
